import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getAttachableAgents,
  getFriends,
  getProjectMembers,
  projectAgentMode,
  projectAttachAgent,
  projectDetachAgent,
  projectInvite,
  projectLeave,
  type AgentMode,
  type AttachableAgent,
  type ProjectMember,
  type ProjectRow,
} from '../transport/api'
import { relayTarget } from '../transport/relay'
import { mergeAttachable } from './projectDynamo'
import { groupRoster, ownerKeyOf } from './rosterGroups'
import { machineNameOf } from '../agents/machineName'
import { RoomPushToggle } from '../push/RoomPushToggle'
import { PixelPet } from '../identity/PixelPet'
import { Avatar } from './Avatar'
import { Icon, type IconName } from '../ui/Icon'
import { Sheet } from '../ui/Sheet'
import { PickerSheet } from '../ui/PickerSheet'
import { limitRefusal } from '../groups/limitText'

/**
 * THE MEMBERS SHEET — who is in the room, whose agents sit under them, and every
 * mutation that changes it (invite, attach, detach, the speak/listen/mute dial,
 * leave). Split out of ProjectPanel.tsx in M6 (拆面板, 2026-09-03), moved verbatim:
 * the census's line was that the roster 跟訊息完全無關 — it shares nothing with the
 * transcript but the project id, and it was 40% of the panel's bulk.
 *
 * The panel keeps its own slim ['proj-members'] subscription for BusyAgents; same
 * key, same fetcher, one cache entry.
 */
/** What a refused invite says — the chat service's codes (POST /conversations/{id}/members). */
function inviteRefusal(code: string): string {
  switch (code) {
    case 'not_friends':
      return 'Only friends can be invited to a project.'
    case 'unknown_member':
      return "That address belongs to more than one account, and we can't tell which one you mean."
    default:
      return limitRefusal(code) ?? `The invite did not go through (${code}).`
  }
}

const AGENT_MODE_DIAL: Array<{ mode: AgentMode; icon: IconName; title: string }> = [
  { mode: 'mute', icon: 'volume_off', title: 'Mute — hears nothing, cannot post' },
  { mode: 'listen', icon: 'hearing', title: 'Listen — hears the room, cannot post' },
  { mode: 'speak', icon: 'chat', title: 'Speak — hears and posts' },
]

export function ProjectRoster({
  project,
  selfEmail,
  serverName,
  open,
  onClose,
  onLeft,
  locked = false,
}: {
  project: ProjectRow
  selfEmail: string | null
  serverName: string
  open: boolean
  onClose: () => void
  onLeft: () => void
  locked?: boolean
}) {
  const qc = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [attachOpen, setAttachOpen] = useState(false)
  const membersQ = useQuery({
    queryKey: ['proj-members', project.project_id],
    queryFn: () => getProjectMembers(project.project_id),
  })
  const members = membersQ.data?.members ?? []
  const humans = members.filter((m) => m.kind !== 'agent')
  // The sheet is a TREE: each human, then the agents they attached seated beneath
  // them (owner, 2026-08-19). Agents nobody in the room owns still render, after.
  const roster = groupRoster(members, selfEmail)
  // Folded owners — in memory, so it survives closing and reopening the sheet
  // while this project is on screen; a reload opens everything again (nothing hidden
  // is the safe default).
  const [folded, setFolded] = useState<Set<string>>(() => new Set())
  const toggleFold = (key: string) =>
    setFolded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const refreshMembers = () =>
    void qc.invalidateQueries({ queryKey: ['proj-members', project.project_id] })

  // Invite candidates: FRIENDS not already in the project — the friend graph
  // is the eligibility source, not the legacy group (owner, 2026-08-16). The
  // server enforces the same rule; this list just doesn't offer people the
  // invite would refuse.
  const friendsQ = useQuery({
    queryKey: ['friends'],
    queryFn: getFriends,
    enabled: inviteOpen,
  })
  /*
   * ONE ROW PER ACCOUNT, NOT PER ADDRESS — owner, 2026-10-31: Project invite people 好像不 work. His
   * friends include both accounts at one address (a Google one and an Apple one): keyed by email the
   * two were one item, and the invite named an address the server cannot resolve to one person. A
   * friend's `account` handle is the key when the list has it; the roster's rows carry the same handle,
   * so the one already here is hidden and the other is still offered.
   */
  const friends = friendsQ.data?.friends ?? []
  const inProjectAccounts = new Set(humans.flatMap((m) => (m.account ? [m.account] : [])))
  const inProjectEmails = new Set(humans.map((m) => String(m.email ?? '').toLowerCase()))
  const inviteId = (f: { email: string; account?: string }) => f.account ?? f.email
  const inviteItems = friends
    .filter((f) => (f.account && inProjectAccounts.size ? !inProjectAccounts.has(f.account) : !inProjectEmails.has(f.email.toLowerCase())))
    .map((f) => ({ id: inviteId(f), label: f.name || f.email, ...(f.name ? { sub: f.email } : {}) }))

  const attachablesQ = useQuery({
    queryKey: ['attachable', project.project_id],
    /*
     * EACH MACHINE'S AGENTS APPEAR AS THAT MACHINE ANSWERS. This sat on the whole
     * fleet sweep and showed "looking for agents…" for 10+ seconds (owner,
     * 2026-08-29) while the slowest unreachable machine timed out — the third
     * surface with this exact fault this week (`@` gate, busy dot). The relay's
     * own machine is asked first and lands in tens of milliseconds; stragglers
     * append. The resolved value still replaces the cache at the end, so a
     * machine that answered nothing drops out then.
     */
    queryFn: () =>
      getAttachableAgents(project.project_id, {
        first: relayTarget(),
        onAgents: (batch) => {
          qc.setQueryData<{ ok: boolean; agents: AttachableAgent[] }>(
            ['attachable', project.project_id],
            (prev) => ({ ok: true, agents: mergeAttachable(prev?.agents, batch) }),
          )
        },
      }),
    enabled: attachOpen,
  })
  const attachItems = (attachablesQ.data?.agents ?? []).map((a) => ({
    id: a.channel_hash,
    label: a.session,
    sub: machineNameOf(a, serverName),
    pet: a.session,
  }))

  const invite = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        const f = friends.find((x) => inviteId(x) === id)
        const r = await projectInvite(project.project_id, f?.email ?? id, f?.account)
        // A refused invite must SAY so, not close the sheet as if it worked (it did, for both
        // accounts at one address, and nobody was invited).
        if (r && r.ok === false) throw new Error(inviteRefusal(r.error ?? 'invite_failed'))
      }
    },
    onSuccess: () => {
      setInviteOpen(false)
      refreshMembers()
    },
    // Back to the roster, where the refusal is shown — and whoever did go in before it, is.
    onError: () => {
      setInviteOpen(false)
      refreshMembers()
    },
  })

  const attach = useMutation({
    mutationFn: async (hashes: string[]) => {
      const byHash = new Map(
        (attachablesQ.data?.agents ?? []).map((a) => [a.channel_hash, a]),
      )
      for (const h of hashes) {
        const a = byHash.get(h)
        if (!a) continue
        // device_id is carried on the DynamoDB attachable list (the cloud roster needs the
        // hosting machine so the feed reaches the agent's pane); undefined on the edge path.
        const r = await projectAttachAgent(
          project.project_id,
          h,
          a.host ? `${a.session} @ ${a.host}` : a.session,
          a.device_id,
        )
        // A refused attach must SAY so, not close the sheet as if it worked.
        if (r && (r as { ok?: boolean }).ok === false) {
          const code = (r as { error?: string }).error ?? 'attach_failed'
          throw new Error(limitRefusal(code) ?? code)
        }
      }
    },
    onSuccess: () => {
      setAttachOpen(false)
      refreshMembers()
    },
    // Some may have gone in before one was refused (a group's agent limit): show the roster as it now is.
    onError: () => refreshMembers(),
  })

  const detach = useMutation({
    mutationFn: (hash: string) => projectDetachAgent(project.project_id, hash),
    onSuccess: refreshMembers,
  })

  /** The owner's dial on each of their agents: speak / listen / mute. */
  const setMode = useMutation({
    mutationFn: async ({ hash, mode }: { hash: string; mode: AgentMode }) => {
      const r = await projectAgentMode(project.project_id, hash, mode)
      /*
       * {ok:false} IS a failure and must THROW, or onError never fires: the
       * Dynamo path catches every transport/API error into {ok:false,error}
       * (projectDynamo), so a refused change resolved "successfully", the
       * rollback never ran, and the "a refused control must SAY so" line below
       * never lit. The CORS-blocked PATCH (avg-main, 2026-08-19) hid behind
       * exactly this: the browser never sent the request and the screen showed
       * nothing at all. The existing refusal test greened against a mock that
       * REJECTS — a shape the real wire never produces (mock-the-wire, again);
       * projectModes.test now pins the {ok:false} shape too.
       */
      if (r && (r as { ok?: boolean }).ok === false) {
        throw new Error((r as { error?: string }).error ?? 'mode_change_refused')
      }
      return r
    },
    // Move the dial the INSTANT it is clicked instead of leaving it looking dead through
    // the PATCH + members-refetch round trip (owner: the buttons "都不能按 / 無效"). The
    // server is authoritative and monotonic; onError rolls a refused change back and the
    // error line shows why, onSettled reconciles against the server's truth.
    onMutate: async ({ hash, mode }) => {
      const key = ['proj-members', project.project_id]
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<{ ok?: boolean; members: ProjectMember[] }>(key)
      if (prev) {
        qc.setQueryData(key, {
          ...prev,
          members: prev.members.map((m) => (m.channel_hash === hash ? { ...m, mode } : m)),
        })
      }
      return { key, prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev)
      void refreshMembers() // a refused change rolled back — pull the server's true state
    },
    // NO refetch on SUCCESS. The `/agents` roster read is eventually consistent, so an
    // immediate refetch right after the PATCH commits can still hit a stale replica and
    // return the OLD mode — which overwrites the optimistic value and snaps the dial back
    // to where it was (owner: 按一下就彈回去原本的狀態). On success the optimistic value
    // already IS the committed mode, so keep it; the next organic refetch (roster reopen /
    // window focus) reconciles once reads settle. The backend read is also switched to
    // strongly-consistent (agentRoster ConsistentRead) so that reconcile can't regress.
  })

  /*
   * A refused control must SAY so. The dial's first live failure (owner:
   * Mute / Listen 的按鈕都不能按) died server-side and this mutation swallowed
   * it — a dead button and a refused one look identical without this line.
   */
  const memberActionError = setMode.error ?? detach.error
  const memberActionErrorText =
    memberActionError == null
      ? null
      : memberActionError instanceof Error
        ? memberActionError.message
        : String(memberActionError)

  /** Two-tap leave, armed on the button itself so the confirmation names the act.
   *  Membership is the router's; failures (e.g. an owner who must transfer first)
   *  come back as text worth reading, so they surface verbatim below. */
  const [leaveArmed, setLeaveArmed] = useState(false)
  const leave = useMutation({
    mutationFn: () => projectLeave(project.project_id),
    onSuccess: (r) => {
      if (r.error) return // surfaced via leave.data check below
      onClose()
      onLeft()
    },
  })
  /** One agent card. `nested` = seated under its owner's row (the tree form), which
   *  drops the redundant "owned by" line — position already says it. */
  const renderAgent = (m: ProjectMember, nested: boolean) => {
    const label = m.label ?? String(m.channel_hash ?? '').slice(0, 8)
    /*
     * The stored label is "session @ host" for remote agents but BARE for
     * local ones (the attach call only suffixes when host is set), so the
     * host is parsed out when present and this edge's name fills the gap.
     * Every agent gets an origin: an unsuffixed row reads as "from
     * nowhere", which is exactly the ambiguity being removed.
     */
    const [name = label, homeServer] = label.split(/\s+@\s+/)
    /*
     * `mine` comes from the EDGE (it attached the agent, so its
     * signature is what the router accepts) — an email comparison
     * cannot work here: the vaultless router names owners by device
     * principal, which no Google session string ever equals. The email
     * check stays as the fallback for a vault-identity world.
     */
    const mine =
      m.mine === true ||
      (!!selfEmail &&
        String(m.owner_email ?? '').toLowerCase() === selfEmail.toLowerCase())
    return (
      <div className={`pr-agent${nested ? ' nested' : ''}`} key={`a:${m.channel_hash}`}>
        <span className="pk-pet">
          <PixelPet name={name} seed={String(m.channel_hash ?? '')} size={26} />
        </span>
        {/* Two lines: WHO on top (name + where it lives), WHOSE below.
            On one line these ran together into an unparseable string —
            name, owner email and badge all fighting for the same row. */}
        <span className="pr-agent-meta">
          {/* Name and home are two items that WRAP: on a phone the indent costs the
              nested card ~20px, and on one line "avg-frontend @ aowl" ellipsised to
              "avg-frontend …" (measured at 430px). Fits → one line; doesn't → the
              host drops to the second line, which a nested card has free. */}
          <span className="pr-agent-name">
            <span className="pr-agent-nm">{name}</span>
            <span className="pr-agent-host">@ {homeServer ?? serverName}</span>
          </span>
          {/* WHOSE, spelled out only when position doesn't already say it —
              a nested card sits under its owner's face. */}
          {!nested && m.owner_email && (
            <span className="pr-agent-owner">owned by {m.owner_email}</span>
          )}
        </span>
        {mine && m.channel_hash ? (
          <>
            {/* The mode dial: three positions, exactly one lit. mute =
                hears nothing & cannot post; listen = hears only;
                speak = full member. Enforced edge-side, stored on the
                router so every device shows the same position. */}
            <span className="pr-modes" role="radiogroup" aria-label={`${name} mode`}>
              {AGENT_MODE_DIAL.map(({ mode, icon, title }) => {
                const on = (m.mode ?? 'speak') === mode
                return (
                  <button
                    key={mode}
                    type="button"
                    className={`pr-mode${on ? ' on' : ''}`}
                    title={title}
                    role="radio"
                    aria-checked={on}
                    onClick={(e) => {
                      // iOS Safari lets a tap on a DISABLED element fall through to
                      // whatever is behind it — here the Sheet's scrim, whose onClose
                      // then fires and shuts the whole roster (owner: tapping Mute/Listen
                      // closed the panel; chromium never reproduced it, WebKit does). So
                      // the dial is NEVER disabled mid-mutation — the optimistic update
                      // plus the `!on` guard already make a repeat tap harmless — and
                      // stopPropagation keeps the click from reaching the scrim at all.
                      e.stopPropagation()
                      if (!on) setMode.mutate({ hash: m.channel_hash!, mode })
                    }}
                  >
                    <Icon name={icon} size={13} label={title} />
                  </button>
                )
              })}
            </span>
            <button
              type="button"
              className="fs-icon fs-danger"
              title="Unattach this agent from the project"
              aria-label={`Unattach ${label}`}
              disabled={detach.isPending}
              onClick={() => detach.mutate(m.channel_hash!)}
            >
              <Icon name="close" size={13} label="Unattach" />
            </button>
          </>
        ) : (
          <span className="pmsg-badge agent">agent</span>
        )}
      </div>
    )
  }
  return (
    <>
      {/* Members — a centered Sheet, not the old full-width band: it used to span
          the whole main area, which on a phone read as a page takeover. The Sheet
          is the app's one modal idiom (centered card, max-width, scrim-tap to
          close), so the roster now matches every other popup. */}
      {open && (
        <Sheet title="Members" onClose={onClose}>
          {/* THE ROOM'S OWN SWITCH, above the people rather than below them.
              Owner, 2026-09-18: 讓下拉選單…上面有一個Checkbox可以允許這個 Project
              Chat 或是 Friend Chat 上面是否要輸送 Notification. It is about the
              ROOM, not about any member, so it sits before the list starts —
              putting it after would read as a property of the last person. */}
          <RoomPushToggle convId={project.project_id} />
          {membersQ.isPending && <div className="pm-empty">loading members…</div>}

          {roster.groups.map(({ human: m, agents: owned }) => {
            // A group's room or a dm hands out an address only between friends (owner, 2026-10-18: 只有加好友之後
            // Email 才會互相顯示) — so a member may come with none, and is then their name.
            const email = String(m.email ?? '')
            const you = !!email && email.toLowerCase() === (selfEmail ?? '').toLowerCase()
            const key = ownerKeyOf(m) || email
            const isFolded = folded.has(key)
            /* The Google photo, same rules as message rows (no-referrer,
               initials fallback on missing/404) — a member list that draws
               letter tiles next to real photos elsewhere reads unfinished
               (owner: 把使用者的 Google Account 頭像放上去). */
            /* THE NAME PEOPLE CHOSE, where there is one — owner, 2026-09-13: 以後
               使用者就可以在其他的地方以 display name 的方式來顯示. The list showed raw
               addresses beside real photographs, which is the one place a person's own
               name is most obviously missing. The address stays reachable (the title,
               and it is still what the row is keyed on) because in a room of colleagues
               two people can pick the same name and the address never collides. */
            // `label` is where the adapter puts a human's name (projectDynamo maps the
            // service's `name` onto it, because the same field carries an agent's label
            // on the other kind of row).
            const shownName = (typeof m.label === 'string' && m.label.trim()) || email || 'Someone'
            const who = you ? 'your' : `${shownName}'s`
            const face = (
              <>
                <Avatar
                  picture={typeof m.picture === 'string' ? m.picture : null}
                  label={shownName}
                  email={email}
                  className="pr-avatar"
                />
                <span className="pr-name" {...(email ? { title: email } : {})}>
                  {shownName}
                  {you ? ' (you)' : ''}
                </span>
              </>
            )
            return (
              <div className="pr-owner" key={`h:${key}`}>
                {owned.length > 0 ? (
                  /* An owner WITH agents is the fold handle itself — the whole row,
                     not a 16px chevron, because on a phone that is the difference
                     between a tap and a miss. The count tells you what's folded
                     when it is. */
                  <button
                    type="button"
                    className="pr-row pr-fold"
                    aria-expanded={!isFolded}
                    aria-label={`${isFolded ? 'Show' : 'Hide'} ${who} agents`}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleFold(key)
                    }}
                  >
                    {face}
                    <span className="pr-fold-n">{owned.length}</span>
                    <Icon name={isFolded ? 'chevron_right' : 'expand_more'} size={16} />
                  </button>
                ) : (
                  <div className="pr-row">{face}</div>
                )}
                {owned.length > 0 && !isFolded && (
                  <div className="pr-owned" role="group" aria-label={`${who} agents`}>
                    {owned.map((a) => renderAgent(a, true))}
                  </div>
                )}
              </div>
            )
          })}

          {/* Agents whose owner is not in the room (they left, or an older wire
              carried no owner at all). Grouping decides WHERE an agent sits, never
              WHETHER it shows. */}
          {roster.orphans.length > 0 && (
            <div className="pr-owner pr-unowned">
              {roster.groups.length > 0 && <div className="pr-section">other agents</div>}
              {roster.orphans.map((a) => renderAgent(a, false))}
            </div>
          )}

          {memberActionErrorText && <div className="err-line pr-err">{memberActionErrorText}</div>}

          <div className="pr-actions">
            {!locked && (
              <button type="button" className="sheet-btn" onClick={() => setInviteOpen(true)}>
                <Icon name="group" size={14} /> Invite people
              </button>
            )}
            <button type="button" className="sheet-btn" onClick={() => setAttachOpen(true)}>
              <Icon name="auto_awesome" size={14} /> Attach agents
            </button>
          </div>

          {/* 退出 — the door out that this room never had. Two-tap: the armed
              state names the project so the confirmation can't be mistaken for a
              different room's. Owners may be refused by the router (transfer
              first); that text shows verbatim below, it's the instruction. */}
          {!locked && (
            <div className="pr-actions pr-leave">
              <button
                type="button"
                className={`sheet-btn${leaveArmed ? ' danger' : ''}`}
                disabled={leave.isPending}
                onClick={() => {
                  if (!leaveArmed) {
                    setLeaveArmed(true)
                    window.setTimeout(() => setLeaveArmed(false), 3000)
                    return
                  }
                  leave.mutate()
                }}
              >
                <Icon name="logout" size={14} />{' '}
                {leave.isPending
                  ? 'leaving…'
                  : leaveArmed
                    ? `Really leave “${project.name ?? project.project_id}”?`
                    : 'Leave project'}
              </button>
            </div>
          )}

          {(invite.error ?? attach.error ?? detach.error ?? leave.error ?? leave.data?.error) && (
            <div className="pm-empty sb-err">
              {String(
                invite.error ?? attach.error ?? detach.error ?? leave.error ?? leave.data?.error,
              )}
            </div>
          )}
        </Sheet>
      )}

      {inviteOpen && (
        <PickerSheet
          title="Invite people"
          placeholder="Search by email…"
          items={inviteItems}
          confirmLabel={(n) => `Invite ${n}`}
          busy={invite.isPending}
          emptyText={
            friendsQ.isPending
              ? 'loading friends…'
              : 'All your friends are already here — add friends to invite more people.'
          }
          onConfirm={(ids) => invite.mutate(ids)}
          onClose={() => setInviteOpen(false)}
        />
      )}

      {attachOpen && (
        <PickerSheet
          title="Attach agents"
          placeholder="Search agents…"
          items={attachItems}
          confirmLabel={(n) => `Attach ${n}`}
          busy={attach.isPending}
          emptyText={
            attachablesQ.isPending ? 'looking for agents…' : 'Every detected agent is already attached.'
          }
          onConfirm={(ids) => attach.mutate(ids)}
          onClose={() => setAttachOpen(false)}
        />
      )}

    </>
  )
}
